import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, User, KeyRound, SlidersHorizontal, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const panelCls = "bg-white rounded-2xl shadow-[0_12px_40px_-12px_rgba(44,38,33,0.25)] border border-neutral-100 p-2";
const anim = { initial: { opacity: 0, y: -8, scale: 0.98 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: -8, scale: 0.98 }, transition: { duration: 0.16, ease: [0.16, 1, 0.3, 1] } };

export default function TopNav({ mode, groups, active, onSelect, preferencesKey }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [openGroup, setOpenGroup] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpenGroup(null); setProfileOpen(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (key) => { onSelect(key); setOpenGroup(null); setMobileOpen(false); };
  const switchMode = (m) => { if (m !== mode) navigate(m === "pos" ? "/admin/pos" : "/admin"); };

  const name = user?.full_name || user?.name || user?.username || user?.email || "Usuario";
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const doPreferences = () => { setProfileOpen(false); setMobileOpen(false); if (preferencesKey && isAdmin) onSelect(preferencesKey); };
  const profileItems = [
    { label: "Mi perfil", icon: User, action: () => { setShowProfile(true); setProfileOpen(false); setMobileOpen(false); }, testid: "profile-view" },
    { label: "Cambiar contraseña", icon: KeyRound, action: () => { setProfileOpen(false); setMobileOpen(false); navigate("/admin/cambiar-clave"); }, testid: "profile-password" },
    ...(isAdmin && preferencesKey ? [{ label: "Preferencias", icon: SlidersHorizontal, action: doPreferences, testid: "profile-preferences" }] : []),
    { label: "Cerrar sesión", icon: LogOut, action: () => { logout(); navigate("/"); }, testid: "profile-logout", danger: true },
  ];

  return (
    <header ref={ref} className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-200/70">
      <div className="h-[70px] px-4 md:px-8 flex items-center justify-between gap-4 max-w-[1600px] mx-auto">
        {/* Left: logo + mode */}
        <div className="flex items-center gap-4 md:gap-6 min-w-0">
          <button onClick={() => navigate(mode === "pos" ? "/admin/pos" : "/admin")} className="flex items-baseline gap-1 shrink-0">
            <span className="font-display text-2xl tracking-[0.14em] text-espresso">MO</span>
            <span className="font-serif italic text-rose text-base hidden sm:inline">by Vir</span>
          </button>
          {isAdmin && (
            <div className="flex bg-neutral-100 rounded-full p-1 shrink-0">
              <ModeBtn label="Punto de Venta" active={mode === "pos"} onClick={() => switchMode("pos")} testid="mode-pos" />
              <ModeBtn label="Admin Web" active={mode === "web"} onClick={() => switchMode("web")} testid="mode-web" />
            </div>
          )}
        </div>

        {/* Center-right: desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {groups.map((g) => {
            const single = g.items.length === 1;
            const groupActive = g.items.some((i) => i.key === active);
            if (single) {
              const it = g.items[0];
              return (
                <button key={g.label} data-testid={`nav-${it.key}`} onClick={() => pick(it.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${active === it.key ? "bg-espresso text-cream" : "text-neutral-600 hover:text-espresso hover:bg-neutral-100"}`}>
                  {it.icon && <it.icon size={16} />} {it.label}
                </button>
              );
            }
            return (
              <div key={g.label} className="relative">
                <button data-testid={`nav-group-${g.key}`} onClick={() => setOpenGroup(openGroup === g.label ? null : g.label)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors ${groupActive ? "bg-espresso text-cream" : "text-neutral-600 hover:text-espresso hover:bg-neutral-100"}`}>
                  {g.label} <ChevronDown size={15} className={`transition-transform ${openGroup === g.label ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {openGroup === g.label && (
                    <motion.div {...anim} className={`absolute left-0 mt-2 w-60 ${panelCls}`}>
                      {g.items.map((it) => (
                        <button key={it.key} data-testid={`nav-${it.key}`} onClick={() => pick(it.key)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active === it.key ? "bg-cream text-espresso font-medium" : "text-neutral-600 hover:bg-neutral-50"}`}>
                          {it.icon && <it.icon size={17} className={active === it.key ? "text-rose" : "text-neutral-400"} />} {it.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* Right: avatar + mobile hamburger */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative hidden lg:block">
            <button data-testid="profile-avatar" onClick={() => setProfileOpen(!profileOpen)}
              className="w-10 h-10 rounded-full bg-rose text-cream text-sm font-medium flex items-center justify-center hover:bg-rose-hover transition-colors">
              {initials}
            </button>
            <AnimatePresence>
              {profileOpen && (
                <motion.div {...anim} className={`absolute right-0 mt-2 w-60 ${panelCls}`}>
                  <div className="px-3 py-2 mb-1 border-b border-neutral-100">
                    <p className="text-sm font-medium text-espresso truncate">{name}</p>
                    <p className="text-xs text-neutral-400">{isAdmin ? "Administrador" : "Cajero"}</p>
                  </div>
                  {profileItems.map((p) => (
                    <button key={p.label} data-testid={p.testid} onClick={p.action}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${p.danger ? "text-red-500 hover:bg-red-50" : "text-neutral-600 hover:bg-neutral-50"}`}>
                      <p.icon size={16} /> {p.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden w-10 h-10 rounded-full hover:bg-neutral-100 flex items-center justify-center text-espresso">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
            className="lg:hidden border-t border-neutral-100 bg-white overflow-hidden">
            <div className="px-4 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {groups.map((g) => (
                <div key={g.label}>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-400 px-2 mb-1">{g.label}</p>
                  <div className="space-y-0.5">
                    {g.items.map((it) => (
                      <button key={it.key} data-testid={`m-nav-${it.key}`} onClick={() => pick(it.key)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active === it.key ? "bg-cream text-espresso font-medium" : "text-neutral-600 hover:bg-neutral-50"}`}>
                        {it.icon && <it.icon size={17} className={active === it.key ? "text-rose" : "text-neutral-400"} />} {it.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="border-t border-neutral-100 pt-3">
                <div className="flex items-center gap-3 px-2 mb-2">
                  <div className="w-9 h-9 rounded-full bg-rose text-cream text-xs font-medium flex items-center justify-center">{initials}</div>
                  <div><p className="text-sm font-medium text-espresso">{name}</p><p className="text-xs text-neutral-400">{isAdmin ? "Administrador" : "Cajero"}</p></div>
                </div>
                {profileItems.map((p) => (
                  <button key={p.label} data-testid={`m-${p.testid}`} onClick={p.action}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${p.danger ? "text-red-500 hover:bg-red-50" : "text-neutral-600 hover:bg-neutral-50"}`}>
                    <p.icon size={16} /> {p.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProfile && (
          <ProfileModal user={user} isAdmin={isAdmin} name={name} initials={initials} onClose={() => setShowProfile(false)} />
        )}
      </AnimatePresence>
    </header>
  );
}

const ModeBtn = ({ label, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all whitespace-nowrap ${active ? "bg-espresso text-cream shadow-sm" : "text-neutral-500 hover:text-espresso"}`}>
    {label}
  </button>
);

function ProfileModal({ user, isAdmin, name, initials, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div {...anim} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-2xl p-6" data-testid="profile-modal">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-rose text-cream text-xl font-medium flex items-center justify-center mb-3">{initials}</div>
          <h3 className="font-serif text-2xl text-espresso">{name}</h3>
          <p className="text-xs uppercase tracking-[0.15em] text-neutral-400 mt-1">{isAdmin ? "Administrador" : "Cajero"}</p>
        </div>
        <div className="mt-5 space-y-2 text-sm">
          {user?.username && <Row l="Usuario" v={user.username} />}
          {user?.email && <Row l="Correo" v={user.email} />}
          {user?.phone && <Row l="Teléfono" v={user.phone} />}
          {user?.register && <Row l="Caja / Sucursal" v={user.register} />}
          {!isAdmin && user?.max_discount_percent != null && <Row l="Descuento máx." v={`${user.max_discount_percent}%`} />}
          {!isAdmin && <Row l="Comprobantes fiscales" v={user?.can_issue_fiscal ? "Permitido" : "No permitido"} />}
        </div>
        <button data-testid="profile-modal-close" onClick={onClose} className="mt-6 w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">Cerrar</button>
      </motion.div>
    </div>
  );
}

const Row = ({ l, v }) => <div className="flex justify-between border-b border-neutral-50 py-1.5"><span className="text-neutral-500">{l}</span><span className="text-espresso font-medium">{v}</span></div>;
